// Places search — spec §3.
//
// ONE function is the entire integration surface: `searchPlaces(query, opts)`.
// Ghost's curated hotels match first (fast, local, hand-picked). If we don't
// have a hit — or the caller is searching for a plain street address — we
// query Photon, the free OSM-backed autocomplete from Komoot. No API key,
// no signup, no rate-limit ceremony.
//
// Contract:
//   query  : the string the user has typed
//   kind   : 'hotel' | 'address' | 'both'  (default 'both')
//   limit  : max results returned (default 6)
// Returns : Array<{
//     id, kind: 'hotel'|'address',
//     hotel, street, city, state, zip, isHotel
//   }>

const CURATED = [
  { hotel: 'Kimpton EPIC Hotel',        street: '270 Biscayne Blvd Way',           city: 'Miami',           state: 'FL', zip: '33131' },
  { hotel: 'The Setai Miami Beach',     street: '2001 Collins Ave',                city: 'Miami Beach',     state: 'FL', zip: '33139' },
  { hotel: 'Faena Hotel',               street: '3201 Collins Ave',                city: 'Miami Beach',     state: 'FL', zip: '33140' },
  { hotel: '1 Hotel South Beach',       street: '2341 Collins Ave',                city: 'Miami Beach',     state: 'FL', zip: '33139' },
  { hotel: 'Fontainebleau Miami Beach', street: '4441 Collins Ave',                city: 'Miami Beach',     state: 'FL', zip: '33140' },
  { hotel: 'Acqualina Resort',          street: '17875 Collins Ave',               city: 'Sunny Isles',     state: 'FL', zip: '33160' },
  { hotel: 'St. Regis Bal Harbour',     street: '9703 Collins Ave',                city: 'Bal Harbour',     state: 'FL', zip: '33154' },
  { hotel: 'W Fort Lauderdale',         street: '401 N Fort Lauderdale Beach Blvd',city: 'Fort Lauderdale', state: 'FL', zip: '33304' },
  { hotel: 'Four Seasons Surf Club',    street: '9011 Collins Ave',                city: 'Surfside',        state: 'FL', zip: '33154' },
]

// Center-of-South-Florida bias for Photon. Same bias for hotels and
// addresses — everything Ghost delivers to is here.
const BIAS_LAT = 26.05
const BIAS_LON = -80.15
const BIAS_SCALE = 1.6

// Photon returns full state names ("Florida"), we render two letters.
const STATE_ABBR = {
  Florida: 'FL', Alabama: 'AL', Georgia: 'GA',
}

function abbr(state) {
  if (!state) return ''
  if (state.length === 2) return state.toUpperCase()
  return STATE_ABBR[state] || state
}

// Score curated hotels/addresses for a local match. Primary field (hotel
// name for hotel mode, street for address mode) counts more than a hit on
// secondary fields, so the first suggestion feels right.
function scoreCurated(pool, q, kind) {
  const out = []
  for (const p of pool) {
    const primary = kind === 'hotel' ? p.hotel : p.street
    const secondary = `${kind === 'hotel' ? p.street : (p.hotel || '')} ${p.city}`
    const priHit = primary.toLowerCase().includes(q)
    const secHit = secondary.toLowerCase().includes(q)
    if (!priHit && !secHit) continue
    out.push({ p, score: priHit ? 2 : 1 })
  }
  out.sort((a, b) => b.score - a.score)
  return out.map(({ p }) => ({
    id: `curated|${p.hotel}|${p.street}|${p.zip}`,
    kind: p.hotel ? 'hotel' : 'address',
    hotel: p.hotel || '',
    street: p.street,
    city: p.city,
    state: p.state,
    zip: p.zip,
    isHotel: !!p.hotel,
  }))
}

// Turn a Photon feature into our result shape. Addresses need
// housenumber+street stitched; some features expose only `name` (POIs,
// hotels). Missing city/zip are common in OSM — return whatever we have,
// the form is still editable.
function fromPhoton(f) {
  const p = f?.properties || {}
  const isHotel = p.osm_key === 'tourism' && (p.osm_value === 'hotel' || p.osm_value === 'motel' || p.osm_value === 'guest_house')

  let street = ''
  if (p.housenumber && p.street) street = `${p.housenumber} ${p.street}`
  else if (p.street) street = p.street
  else if (p.name && !isHotel) street = p.name

  const city = p.city || p.town || p.village || p.locality || ''
  const state = abbr(p.state || '')
  const zip = p.postcode || ''
  const hotel = isHotel ? (p.name || '') : ''

  return {
    id: `photon|${p.osm_type || ''}${p.osm_id || Math.random()}`,
    kind: isHotel ? 'hotel' : 'address',
    hotel,
    street,
    city,
    state,
    zip,
    isHotel,
  }
}

async function photon(q, kind, limit) {
  const params = new URLSearchParams({
    q,
    limit: String(limit),
    lang: 'en',
    lat: String(BIAS_LAT),
    lon: String(BIAS_LON),
    location_bias_scale: String(BIAS_SCALE),
  })
  if (kind === 'hotel') params.append('osm_tag', 'tourism:hotel')
  const url = `https://photon.komoot.io/api/?${params.toString()}`
  const res = await fetch(url)
  if (!res.ok) return []
  const data = await res.json()
  const features = Array.isArray(data?.features) ? data.features : []
  const rows = features
    .map(fromPhoton)
    // For hotel mode we only want hotels; for address mode drop pure POIs
    // that have no usable street line.
    .filter((r) => (kind === 'hotel' ? r.isHotel : r.street))
    // Ghost's service area — country=USA, state=FL. If Photon returns
    // something outside FL we let it through (the customer is entering
    // it themselves), but rank it lower via `state` display.
  return rows
}

/**
 * @param {string} query
 * @param {{ kind?: 'hotel'|'address'|'both', limit?: number }} [opts]
 * @returns {Promise<Array<object>>}
 */
export async function searchPlaces(query, opts = {}) {
  const kind = opts.kind || 'both'
  const limit = opts.limit || 6
  const q = String(query || '').trim().toLowerCase()
  if (q.length < 2) return []

  const curatedPool = kind === 'hotel' ? CURATED.filter((p) => p.hotel) : CURATED
  const local = scoreCurated(curatedPool, q, kind === 'hotel' ? 'hotel' : 'address')

  let remote = []
  try {
    remote = await photon(query, kind, limit)
  } catch { /* offline / blocked — curated only */ }

  // Curated first, then remote, deduped on street+zip.
  const seen = new Set()
  const merged = []
  for (const r of [...local, ...remote]) {
    const key = `${r.street}|${r.zip}`.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(r)
    if (merged.length >= limit) break
  }
  return merged
}
